import {
  ExternalRouteRef,
  RouteRef,
  SubRouteRef,
  createExternalRouteRef,
} from '@backstage/core-plugin-api';

import {
  createRootRouteRefsPair,
  createSubRouteRefsPair,
} from '@spotify/confidence-core-react';
import {
  ExternalRoutesIds as MetricsReactExternalRoutesIds,
  entitiesRouteRef,
  factTablesCreateRouteRef,
} from '@spotify/plugin-confidence-metrics-react';

export enum RoutesIds {
  metrics = 'metrics',
  metric = 'metric',
  measurements = 'measurements',
  measurement = 'measurement',
}

enum ExternalRoutesIds {
  workflowInstance = 'workflowInstance',
  entity = 'entity',
  factTables = 'factTables',
  factTable = 'factTable',
  assignmentTable = 'assignmentTable',
  assignmentTablesCreate = 'assignmentTablesCreate',
  metricsDataWarehouse = 'metricsDataWarehouse',
  labels = 'labels',
}

// root route
export const [metricsRouteImplRef, metricsRouteRef] = createRootRouteRefsPair({
  id: RoutesIds.metrics,
});

export const rootRouteRef = metricsRouteImplRef;

// sub-routes within the plugin
export const [metricRouteImplRef, metricRouteRef] = createSubRouteRefsPair({
  id: RoutesIds.metric,
  path: '/:id',
  parent: rootRouteRef,
});

// measurements root route (separate from metrics)
export const [measurementsRouteImplRef, measurementsRouteRef] =
  createRootRouteRefsPair({
    id: RoutesIds.measurements,
  });

export const [measurementRouteImplRef, measurementRouteRef] =
  createSubRouteRefsPair({
    id: RoutesIds.measurement,
    path: '/:id',
    parent: measurementsRouteImplRef,
  });

export const workflowInstanceRouteRef = createExternalRouteRef({
  id: ExternalRoutesIds.workflowInstance,
  params: ['workflow', 'id'],
  optional: true,
});

// external routes to other plugins
export const factTablesRouteRef = createExternalRouteRef({
  id: ExternalRoutesIds.factTables,
  optional: true,
});

export const factTableRouteRef = createExternalRouteRef({
  id: ExternalRoutesIds.factTable,
  params: ['id'],
  optional: true,
});

// entitiesRouteRef and factTablesCreateRouteRef are imported from confidence-metrics-react
export { entitiesRouteRef, factTablesCreateRouteRef };

export const entityRouteRef = createExternalRouteRef({
  id: ExternalRoutesIds.entity,
  params: ['id'],
  optional: true,
});

export const assignmentTableRouteRef = createExternalRouteRef({
  id: ExternalRoutesIds.assignmentTable,
  params: ['id'],
  optional: true,
});

export const assignmentTablesCreateRouteRef = createExternalRouteRef({
  id: ExternalRoutesIds.assignmentTablesCreate,
  optional: true,
});

export const metricsDataWarehouseRouteRef = createExternalRouteRef({
  id: ExternalRoutesIds.metricsDataWarehouse,
  optional: true,
});

export const labelsRouteRef = createExternalRouteRef({
  id: ExternalRoutesIds.labels,
  optional: true,
});

// default routes/routes to be used by other plugins
export const Routes = {
  metrics: metricsRouteImplRef,
  metric: metricRouteImplRef,
  measurements: measurementsRouteImplRef,
  measurement: measurementRouteImplRef,
} satisfies Record<RoutesIds, SubRouteRef | RouteRef>;

export const ExternalRoutes = {
  // sub-routes within the plugin
  metrics: metricsRouteRef,
  metric: metricRouteRef,
  measurements: measurementsRouteRef,
  measurement: measurementRouteRef,

  // routes to other plugins
  workflowInstance: workflowInstanceRouteRef,
  factTable: factTableRouteRef,
  factTables: factTablesRouteRef,
  factTablesCreate: factTablesCreateRouteRef,
  entities: entitiesRouteRef,
  entity: entityRouteRef,
  assignmentTable: assignmentTableRouteRef,
  assignmentTablesCreate: assignmentTablesCreateRouteRef,
  metricsDataWarehouse: metricsDataWarehouseRouteRef,
  labels: labelsRouteRef,
} satisfies Record<RoutesIds, ExternalRouteRef> &
  Record<ExternalRoutesIds, ExternalRouteRef> &
  Record<MetricsReactExternalRoutesIds, ExternalRouteRef>;
